import { bitable } from '@lark-base-open/js-sdk';
import type { FieldMeta, RecordRange, TableContext } from './types';
import { BitableApiError } from './types';

function wrap<T>(code: string, message: string, fn: () => Promise<T>): Promise<T> {
  return fn().catch((err: unknown) => {
    throw new BitableApiError(code, message, err);
  });
}

export async function getActiveTableContext(): Promise<TableContext> {
  return wrap('GET_ACTIVE_CONTEXT_FAILED', '获取当前数据表上下文失败', async () => {
    const selection = await bitable.base.getSelection();
    if (!selection.tableId) {
      throw new Error('selection.tableId is null');
    }
    let viewId = selection.viewId;
    if (!viewId) {
      const table = await bitable.base.getActiveTable();
      const view = await table.getActiveView();
      viewId = view.id;
    }
    return { tableId: selection.tableId, viewId: viewId ?? '' };
  });
}

/** 个人 Base 令牌：只对当前 Base 有效，独立页的图片靠它让 worker 现取下载地址。拿不到返回空。 */
export async function getPersonalBaseTokenSafe(): Promise<string> {
  try {
    return (await bitable.bridge.getPersonalBaseToken()) || '';
  } catch {
    return '';
  }
}
export async function getBaseIdSafe(): Promise<string> {
  try {
    return (await bitable.base.getSelection()).baseId || '';
  } catch {
    return '';
  }
}

/** 企业标识：团队共享模板按它分区。 */
export async function getTenantKey(): Promise<string> {
  return wrap('GET_TENANT_KEY_FAILED', '获取企业标识失败', async () => {
    return bitable.bridge.getTenantKey();
  });
}

/** Stable per-user id, used as the billing account key. */
export async function getBaseUserId(): Promise<string> {
  return wrap('GET_BASE_USER_ID_FAILED', '获取用户标识失败', async () => {
    return bitable.bridge.getBaseUserId();
  });
}

/** Opaque Base instance id（签字确认服务用它 scope 确认单列表）。 */
export async function getBaseInstanceId(): Promise<string> {
  return wrap('GET_INSTANCE_ID_FAILED', '获取 Base 标识失败', async () => {
    return bitable.bridge.getInstanceId();
  });
}

/** 放大 / 还原插件在飞书里的宿主容器（全屏编辑器用）。失败静默——
 *  某些容器/旧版不支持时，浮层仍以当前面板尺寸展示。 */
export async function setHostContainerLarge(large: boolean): Promise<void> {
  try {
    const ui = (bitable as unknown as {
      ui?: { setHostContainerSize?: (s: string) => Promise<boolean> };
    }).ui;
    await ui?.setHostContainerSize?.(large ? 'large' : 'small');
  } catch {
    /* 不支持就维持原尺寸 */
  }
}

export async function getActiveRecordId(): Promise<string | null> {
  return wrap('GET_ACTIVE_RECORD_ID_FAILED', '获取当前选中记录失败', async () => {
    const selection = await bitable.base.getSelection();
    return selection.recordId ?? null;
  });
}

export async function getTableName(tableId: string): Promise<string> {
  return wrap('GET_TABLE_NAME_FAILED', '获取表名失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    return table.getName();
  });
}

export async function getFieldMetaList(tableId: string): Promise<FieldMeta[]> {
  return wrap('GET_FIELD_META_LIST_FAILED', '获取字段元数据失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    return table.getFieldMetaList();
  });
}

export async function getVisibleFieldIds(tableId: string, viewId: string): Promise<string[]> {
  return wrap('GET_VISIBLE_FIELD_IDS_FAILED', '获取视图可见字段失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    const view = await table.getViewById(viewId);
    return view.getVisibleFieldIdList();
  });
}

export async function getRecordIds(
  tableId: string,
  viewId: string,
  range: RecordRange,
): Promise<string[]> {
  return wrap('GET_RECORD_IDS_FAILED', '获取记录列表失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    if (range === 'all') {
      return table.getRecordIdList();
    }
    if (range === 'visible') {
      const view = await table.getViewById(viewId);
      const ids = await view.getVisibleRecordIdList();
      return ids.filter((id): id is string => typeof id === 'string');
    }
    // 'selected' — prefer the Grid-view multi-row checkbox selection
    // (view.getSelectedRecordIdList), then fall back to the single-cell
    // cursor record. The cursor fallback handles non-Grid views (Kanban,
    // Gallery, …) where the multi-row checkbox API doesn't exist.
    const view = await table.getViewById(viewId);
    const viewWithSelection = view as unknown as {
      getSelectedRecordIdList?: () => Promise<string[]>;
    };
    if (typeof viewWithSelection.getSelectedRecordIdList === 'function') {
      try {
        const ids = await viewWithSelection.getSelectedRecordIdList();
        if (ids.length > 0) return ids;
      } catch {
        // fall through to cursor
      }
    }
    const selection = await bitable.base.getSelection();
    return selection.recordId ? [selection.recordId] : [];
  });
}

/** Read multi-row checkbox selection (Grid view only). Falls back to the
 *  cell-cursor record so callers that just want "the user's intended row"
 *  always get something. Returns empty array when neither is available. */
export async function getSelectedRecordIds(
  tableId: string,
  viewId: string,
): Promise<string[]> {
  return wrap(
    'GET_SELECTED_RECORD_IDS_FAILED',
    '获取勾选记录失败',
    async () => {
      const table = await bitable.base.getTableById(tableId);
      const view = await table.getViewById(viewId);
      const viewWithSelection = view as unknown as {
        getSelectedRecordIdList?: () => Promise<string[]>;
      };
      if (typeof viewWithSelection.getSelectedRecordIdList === 'function') {
        try {
          const ids = await viewWithSelection.getSelectedRecordIdList();
          if (ids.length > 0) return ids;
        } catch {
          // ignore
        }
      }
      const selection = await bitable.base.getSelection();
      return selection.recordId ? [selection.recordId] : [];
    },
  );
}

/** Read ONLY checkbox-selected records — does NOT fall back to the cell
 *  cursor. Used by the polling loop so we can tell "user has nothing
 *  checked" apart from "user has checked some rows". Returns [] silently
 *  on any error (poll loop shouldn't blow up the UI). */
/**
 * 视图里被勾选的记录。**出错返回 null，不是空数组** —— 轮询方把空数组当成
 * 「用户取消了勾选」，一次瞬时报错就会让预览莫名跳到第一行。
 */
export async function getCheckedRecordIds(
  tableId: string,
  viewId: string,
): Promise<string[] | null> {
  try {
    const table = await bitable.base.getTableById(tableId);
    const view = await table.getViewById(viewId);
    const v = view as unknown as {
      getSelectedRecordIdList?: () => Promise<string[]>;
    };
    if (typeof v.getSelectedRecordIdList !== 'function') return null;
    return await v.getSelectedRecordIdList();
  } catch {
    return null;
  }
}

export async function getCellValue(
  tableId: string,
  fieldId: string,
  recordId: string,
): Promise<unknown> {
  return wrap('GET_CELL_VALUE_FAILED', '获取单元格值失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    return table.getCellValue(fieldId, recordId);
  });
}

/**
 * 整条记录一次取（getRecordById），只挑要的字段。
 * 以前是一格一格 getCellValue：一条记录十几个字段就是十几次 SDK 往返，
 * 明细表几十行下来几百次，打开编辑器要等好几秒。取不到整条时退回逐格。
 */
async function readRecord(
  table: Awaited<ReturnType<typeof bitable.base.getTableById>>,
  recordId: string,
  fieldIds: string[],
): Promise<Record<string, unknown>> {
  try {
    const rec = (await table.getRecordById(recordId)) as { fields?: Record<string, unknown> } | null;
    if (rec && rec.fields && typeof rec.fields === 'object') {
      const out: Record<string, unknown> = {};
      for (const fid of fieldIds) out[fid] = rec.fields[fid] ?? null;
      return out;
    }
  } catch {
    /* 退回逐格 */
  }
  const entries = await Promise.all(
    fieldIds.map(async (fid) => {
      try {
        const v = await table.getCellValue(fid, recordId);
        return [fid, v] as const;
      } catch {
        return [fid, null] as const;
      }
    }),
  );
  return Object.fromEntries(entries);
}

/** 有限并发跑一组任务：SDK 的桥接一次塞太多请求会排队甚至超时。 */
async function mapLimit<T, R>(items: T[], limit: number, fn: (x: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  });
  await Promise.all(workers);
  return out;
}

export async function getRecordCells(
  tableId: string,
  recordId: string,
  fieldIds: string[],
): Promise<Record<string, unknown>> {
  return wrap('GET_RECORD_CELLS_FAILED', '获取记录字段值失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    return readRecord(table, recordId, fieldIds);
  });
}


export async function getLinkedRecords(
  tableId: string,
  recordIds: string[],
  fieldIds: string[],
): Promise<Array<{ recordId: string; cells: Record<string, unknown> }>> {
  return wrap('GET_LINKED_RECORDS_FAILED', '获取关联记录失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    // 整条记录一次取、六路并发；以前是逐行逐格串行
    return mapLimit(recordIds, 6, async (recordId) => ({
      recordId,
      cells: await readRecord(table, recordId, fieldIds),
    }));
  });
}

/** Resolve an attachment cell's tokens into short-lived download URLs.
 *  Returns [] on any failure — a broken attachment must not kill the render. */
export async function getCellAttachmentUrls(
  tableId: string,
  fieldId: string,
  recordId: string,
  tokens: string[],
): Promise<string[]> {
  if (tokens.length === 0) return [];
  try {
    const table = await bitable.base.getTableById(tableId);
    const t = table as unknown as {
      getCellAttachmentUrls?: (tokens: string[], fieldId: string, recordId: string) => Promise<string[]>;
    };
    if (typeof t.getCellAttachmentUrls !== 'function') return [];
    return await t.getCellAttachmentUrls(tokens, fieldId, recordId);
  } catch {
    return [];
  }
}

/**
 * 把生成好的成品存进记录的附件字段。
 *
 * 侧边栏此前只能打印、导出、批量、签名 —— 生成的 PDF 只落在用户的下载文件夹，
 * 没有回到表里。而单据最该待的地方就是那条记录上：谁都能点开看，不用去翻别人
 * 的电脑。竞品的输出区有「存附件」勾选，这是我们缺的一环。
 *
 * 走 SDK 的 batchUploadFile 换 token 再写单元格。不直接把 File 塞给
 * setCellValue：那条路在部分版本上不转换，写进去是空值，而且拿不到 token
 * 也就没法做「追加」（追加要把旧附件的 token 一起写回去）。
 */
export async function saveFileToAttachmentCell(
  tableId: string,
  fieldId: string,
  recordId: string,
  file: File,
  mode: 'append' | 'replace',
): Promise<void> {
  // 不走 wrap：它把原因吞成一句「存入附件字段失败」，用户和我们都看不出是权限还是上传
  try {
    await doSaveAttachment(tableId, fieldId, recordId, file, mode);
  } catch (err) {
    const why = err instanceof Error ? err.message : String(err);
    throw new BitableApiError('SAVE_ATTACHMENT_FAILED', `存入附件字段失败：${why}`, err);
  }
}

async function doSaveAttachment(
  tableId: string,
  fieldId: string,
  recordId: string,
  file: File,
  mode: 'append' | 'replace',
): Promise<void> {
  const table = await bitable.base.getTableById(tableId);
  const token = await uploadWithRetry(file);

  type Att = { token: string; name: string; size: number; type: string; timeStamp: number };
  const next: Att[] = [];
  if (mode === 'append') {
    const prev = await table.getCellValue(fieldId, recordId).catch(() => null);
    if (Array.isArray(prev)) {
      for (const a of prev) {
        if (a && typeof a === 'object' && typeof (a as { token?: string }).token === 'string') {
          next.push(a as unknown as Att);
        }
      }
    }
  }
  // timeStamp 是 IOpenAttachment 的必填项：缺了它宿主可能整条拒收，
  // 而 setCellValue 只回一个 false —— 以前没看返回值，界面照样报「已存入」
  next.push({ token, name: file.name, size: file.size, type: file.type || 'application/pdf', timeStamp: Date.now() });
  const ok = await table.setCellValue(fieldId, recordId, next as unknown as never);
  if (ok === false) {
    throw new Error('多维表格拒绝了这次写入：可能你对这条记录或这个附件字段没有编辑权限（高级权限 / 只读视图），请换有权限的账号或请管理员放开');
  }
  // 读回来确认文件真的在格子里，别再「提示成功、格子是空的」
  // 宿主可能给附件换 token，名字 + 大小对上也算；刚写完读可能还没落，隔一会儿再读一次
  const landed = async () => {
    const after = await table.getCellValue(fieldId, recordId).catch(() => null);
    return (
      Array.isArray(after) &&
      after.some((a) => {
        const x = a as { token?: string; name?: string; size?: number };
        return x?.token === token || (x?.name === file.name && x?.size === file.size);
      })
    );
  };
  if (!(await landed()) && !(await new Promise((r) => setTimeout(r, 1200)).then(landed))) {
    throw new Error('写入后在附件字段里没找到这份文件：请确认这条记录没有被删除、字段没有被改成其他类型，然后再试一次');
  }
}

/**
 * batchUploadFile 并发调用会把宿主桥接搞乱，偶尔静默返回 [null]（banana 插件线上踩过）。
 * 全局串行 + 失败重试三次。
 */
let uploadQueue: Promise<unknown> = Promise.resolve();
async function uploadWithRetry(file: File): Promise<string> {
  const uploader = bitable.base as unknown as { batchUploadFile?: (files: File[]) => Promise<string[]> };
  if (typeof uploader.batchUploadFile !== 'function') {
    throw new Error('当前多维表格版本不支持从插件上传附件，请更新飞书后重试');
  }
  const run = async () => {
    let last = '';
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const [token] = await uploader.batchUploadFile!([file]);
        if (typeof token === 'string' && token) return token;
        last = '上传没有返回文件标识';
      } catch (e) {
        last = e instanceof Error ? e.message : String(e);
      }
      if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 2000));
    }
    throw new Error(`文件上传失败（已重试 3 次）：${last}`);
  };
  const p = uploadQueue.catch(() => undefined).then(run);
  uploadQueue = p;
  return p;
}

/**
 * 默认存到哪个附件字段。以前一律取第一个附件字段 —— 表里有「附件」「盖章件」两个时，
 * 文件进了「附件」，用户在「盖章件」里找不到。顺序：这张表上次选的 → 名字像成品的 → 第一个。
 */
const ATT_FIELD_KEY = 'pp-attach-field:';
export function pickAttachmentField(tableId: string | undefined, fields: Array<{ id: string; name: string }>): string {
  if (!fields.length) return '';
  try {
    const remembered = tableId ? localStorage.getItem(ATT_FIELD_KEY + tableId) : null;
    if (remembered && fields.some((f) => f.id === remembered)) return remembered;
  } catch {
    /* 存储不可用就按名字猜 */
  }
  const byName = fields.find((f) => /盖章|签章|用印|回传|成品|单据|PDF|pdf|打印/.test(f.name));
  return (byName ?? fields[0]).id;
}
export function rememberAttachmentField(tableId: string | undefined, fieldId: string): void {
  if (!tableId || !fieldId) return;
  try {
    localStorage.setItem(ATT_FIELD_KEY + tableId, fieldId);
  } catch {
    /* 记不住也不影响这次 */
  }
}

export async function getRecordIdsByTable(tableId: string): Promise<string[]> {
  return wrap('GET_RECORD_IDS_BY_TABLE_FAILED', '获取记录列表失败', async () => {
    const table = await bitable.base.getTableById(tableId);
    return table.getRecordIdList();
  });
}

export function onContextChange(cb: (ctx: TableContext) => void): () => void {
  // 用当前上下文做种。种成 null 的话，打开插件后第一次点任意单元格都会被当成
  // 「切表了」，触发重新加载 —— 用户刚调好的字段勾选和排序当场被重置。
  let lastTableId: string | null = null;
  let lastViewId: string | null = null;
  void bitable.base
    .getSelection()
    .then((sel) => {
      if (lastTableId === null && sel?.tableId) {
        lastTableId = sel.tableId;
        lastViewId = sel.viewId ?? null;
      }
    })
    .catch(() => {
      /* 拿不到就退回旧行为 */
    });
  const unsubscribe = bitable.base.onSelectionChange((event) => {
    const tableId = event.data.tableId;
    const viewId = event.data.viewId;
    if (!tableId || !viewId) return;
    if (tableId === lastTableId && viewId === lastViewId) return;
    lastTableId = tableId;
    lastViewId = viewId;
    cb({ tableId, viewId });
  });
  return unsubscribe;
}

/** Fires whenever the user's record selection changes in the host table —
 *  e.g. they click a different row in Lark Base. Emits null when no record
 *  is currently selected (e.g. column header focus). */
export function onRecordSelectionChange(
  cb: (recordId: string | null) => void,
): () => void {
  let last: string | null | undefined = undefined;
  const unsubscribe = bitable.base.onSelectionChange((event) => {
    const next = event.data.recordId ?? null;
    if (next === last) return;
    last = next;
    cb(next);
  });
  return unsubscribe;
}
